import { WorkoutAchievementType } from "../../../enums";
import { MuscleGroup } from "./muscleGroups";

export interface IWorkoutSetViewModel {
  id: number;
  kg: number;
  reps: number;
  isCompleted: boolean;
  position: number;
  previousKg: number | null;
  previousReps: number | null;
  /** Band resistance in kg on a resistance-band exercise; null when not logged. */
  resistanceKg?: number | null;
  /** Speed in km/h on a Time + Pace exercise (the time is `reps`, in seconds). */
  speedKmh?: number | null;
  previousResistanceKg?: number | null;
  previousSpeedKmh?: number | null;
  /** Personal-record badges this completed set currently holds (drives the trophy marker). */
  achievements: WorkoutAchievementType[];
  clientKey?: string;
}

/** One badge a just-completed set earned. Mirrors `WorkoutSetAchievementViewModel`. */
export interface IWorkoutSetAchievement {
  type: WorkoutAchievementType;
  /** Kg for MaxWeight, kg × reps for MaxSetVolume. */
  value: number;
  /** The previous best that was beaten; null for a first record (the first completed set on the exercise). */
  previousValue: number | null;
}

/** Mirrors `ToggleSetCompletedResultViewModel` — the response of ToggleSetCompleted. */
export interface IToggleSetCompletedResult {
  achievements: IWorkoutSetAchievement[];
}

/** Mirrors `Planny.Common.Enums.WorkoutExerciseType` on the backend. */
export enum WorkoutExerciseType {
  KgReps = 0,
  /** Seconds, stored in `reps`. */
  Time = 1,
  /** Kg × metres (metres in `reps`), entered and shown in the exercise's `distanceUnit`. */
  Distance = 2,
  /** Seconds in `reps` plus a speed in km/h (`speedKmh`). For running and other cardio. */
  TimePace = 3,
}

/** Mirrors `Planny.Common.Enums.WorkoutSection`. Shown in the order WarmUp, Workout, CoolDown. */
export enum WorkoutSection {
  Workout = 0,
  WarmUp = 1,
  CoolDown = 2,
}

/** Mirrors `Planny.Common.Enums.DistanceUnit`. Sets always store metres. */
export enum DistanceUnit {
  Meters = 0,
  Kilometers = 1,
}

export interface IWorkoutPlanExerciseViewModel {
  id: number;
  exerciseId: number;
  exerciseName: string;
  notes: string | null;
  position: number;
  /** Per-exercise rest timer between sets, in seconds. 0 = OFF. */
  restTimerSeconds: number;
  /** When true, the rest timer auto-starts as soon as a set is marked completed. */
  useAutomaticTimer: boolean;
  /** Single uppercase letter A-Z or null. Drives the "A1, A2, …" prefix on set numbers. */
  supersetLetter: string | null;
  /** How sets are measured. Controls the column layout in the row. */
  exerciseType: WorkoutExerciseType;
  /** Warm-up, main workout or cool-down. */
  section: WorkoutSection;
  /** The unit a Distance exercise is entered and shown in. */
  distanceUnit: DistanceUnit;
  /** The underlying exercise logs a band resistance on every set (shared flag on the exercise). */
  isResistanceBand: boolean;
  /** Muscle groups the underlying exercise targets (shared tag on the exercise). */
  muscleGroups: MuscleGroup[];
  /**
   * True when the underlying exercise comes from the read-only system catalog. Its
   * name, muscle groups and type are locked (not editable), even for admins/owners.
   */
  isSystemDefined: boolean;
  sets: IWorkoutSetViewModel[];
  clientKey?: string;
}

export interface IWorkoutPlanViewModel {
  id: number;
  /** Event id, or null for a standalone (event-less) workout. */
  eventId: number | null;
  /** User-supplied name for the workout. When null/empty the UI shows a placeholder. */
  name: string | null;
  /**
   * The user whose history the workout is shown against (ПРЕДИШНА, trophies). 0 when there is
   * none — no registration yet, or several people registered and nobody named.
   */
  registeredUserId: number;
  exercises: IWorkoutPlanExerciseViewModel[];
  /** Id of the root workout this one was derived from. Null = standalone or empty plan. */
  lineageRootId: number | null;
  /** This plan's 1-based position in the lineage. Null when standalone/empty. */
  lineageNumber: number | null;
  /** Total workouts in this lineage. Null when standalone/empty. */
  lineageTotalCount: number | null;
  /** Root workout's name. Null when the root has no name (UI falls back to a generic label). */
  lineageRootName: string | null;
}

export interface IExerciseSearchResultModel {
  id: number;
  name: string;
  hasUserHistory: boolean;
  lastKg: number | null;
  lastReps: number | null;
  lastSpeedKmh?: number | null;
  /** How the last set was measured; it decides how lastKg / lastReps read. */
  lastExerciseType?: WorkoutExerciseType | null;
  lastDistanceUnit?: DistanceUnit | null;
}

/**
 * How the workout dialog behaves:
 *  - "admin"  — full editing, including creating brand-new studio exercises.
 *  - "owner"  — the workout's owner (a trainee editing their own workout, or the
 *               creator of a standalone workout). Full editing parity with the admin
 *               EXCEPT they can only add existing studio exercises, never create new ones.
 *  - "user"   — read-mostly preview (e.g. an upcoming reservation): view + log reps.
 */
export type WorkoutPlanDialogMode = "admin" | "owner" | "user";

export interface IUserWorkoutListItemViewModel {
  id: number;
  eventId: number;
  eventName: string;
  workoutName: string | null;
  date: string;
  /** 1-based position in the workout's lineage. Null for standalone workouts. */
  lineageNumber: number | null;
}
